import { QueryClient, dehydrate } from "@tanstack/react-query";
import { GetServerSideProps } from "next";
import absoluteUrl from "next-absolute-url";
import { Trans, useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import { PushNotificationAlert } from "@filadd/phi-plus-layout";
import { css } from "@filadd/phi-plus-styled-system/css";

import { useAuth } from "AuthContext";
import { studyProgramPath } from "helpers/redirects";
import { getCurrentRelease } from "helpers/release";
import { useCourse } from "hooks/courses-new/course";
import { useStudyProgramsByCourse } from "hooks/courses-new/study-program";
import { fetchDiagnosticsInfo } from "hooks/diagnostics";
import { fetchReleaseMembership } from "hooks/membership";
import { fetchNpsData, postNpsSubmission } from "hooks/nps";
import { useUserCourseAccess } from "hooks/users/release";

import {
  Search,
  StudyProgramList,
  UserPendingDiagnostic,
} from "components/content";
import { CommonLayout } from "components/commons";

import { CustomGetServerSidePropsContext } from "types/request";
import type { UserCourseAccessResponse } from "types/users/release";

interface Props {
  courseSlug: string;
}

const CourseContentView = ({ courseSlug }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: courseData, isLoading: isCourseLoading } = useCourse({
    params: { field: "slug", value: courseSlug },
  });
  const { data: userCourseAccess } = useUserCourseAccess({
    params: { course_slug: courseSlug },
    enabled: !!user,
  });

  if (isCourseLoading) {
    return <p>Loading...</p>;
  }

  if (!courseData) {
    return <p>Course not found</p>;
  }

  return (
    <CommonLayout>
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
        })}
      >
        <h2
          className={css({
            textStyle: "titleLarge",
          })}
        >
          {t("common:subjects")}
        </h2>

        {userCourseAccess && (
          <Search
            courseSlug={courseSlug}
            accessType={userCourseAccess.access_type}
          />
        )}
      </div>

      {userCourseAccess && userCourseAccess.access_type !== "NO_ACCESS" && (
        <div>
          <PushNotificationAlert
            title={t("notifications:push.title")}
            description={
              <Trans t={t} i18nKey="notifications:push.description" />
            }
            actionLabel={t("notifications:push.actionLabel")}
            dismissLocalStorageKey="push-notification-main-page-alert-dismissed"
            className={css({
              mb: 4,
              width: "full",
            })}
          />

          <UserPendingDiagnostic courseSlug={courseSlug} />
        </div>
      )}

      <StudyProgramList courseSlug={courseSlug} />
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  ctx: CustomGetServerSidePropsContext,
) => {
  const queryClient = new QueryClient();
  const { userData } = ctx.req;
  const courseSlug = ctx.query.courseSlug as string | undefined;

  // Something really terrible must have happened if this is undefined
  if (!courseSlug) {
    return {
      notFound: true,
    };
  }

  const courseData = await useCourse.fetchQuery(queryClient, {
    ctx,
    params: {
      field: "slug",
      value: courseSlug,
    },
  });

  if (!courseData) {
    // Something really terrible must have happened if this is undefined

    return {
      notFound: true,
    };
  }

  let userCourseAccess: UserCourseAccessResponse | undefined;
  if (userData) {
    userCourseAccess = await useUserCourseAccess.fetchQuery(queryClient, {
      ctx,
      params: { course_slug: courseSlug },
    });
  }

  const currentRelease = getCurrentRelease(
    userData ? userData.releases : [],
    courseSlug,
  );
  const releaseId = currentRelease ? currentRelease.id : null;
  const releaseMembership = releaseId
    ? await fetchReleaseMembership(releaseId, ctx)
    : null;
  const npsData = releaseId ? await fetchNpsData(courseData.id, ctx) : null;

  const getContinueUrl = () => {
    const { protocol, host } = absoluteUrl(ctx.req);
    const currentPath = ctx.resolvedUrl;
    const continueUrl = `${protocol}//${host}${currentPath}`;

    return continueUrl;
  };

  const getPaperformPrefilledData = () => {
    if (!userData || !releaseMembership || !currentRelease) {
      return "";
    }

    const paperformSignature = currentRelease.paperform_signature;

    if (!paperformSignature) {
      return "";
    }

    const paperformUserData = {
      user_id: `${userData.id}`,
      user_email: encodeURIComponent(userData.email),
      user_fullname: userData.fullname,
      signature: paperformSignature,
    };

    const paperformCourseData = {
      course_id: `${courseData.id}`,
      course_slug: courseData.slug,
      course_title: courseData.title,
      release_id: `${releaseId}`,
      membership_id: `${releaseMembership.id}`,
      membership_category: releaseMembership.category,
      membership_title: releaseMembership.title,
      continue_url: getContinueUrl(),
    };

    return new URLSearchParams({
      ...paperformUserData,
      ...paperformCourseData,
    }).toString();
  };

  if (npsData) {
    const formId = npsData.nps_form_id;
    const queryParameters = getPaperformPrefilledData();

    try {
      await postNpsSubmission(
        {
          course_id: courseData.id,
          nps_form_id: formId,
          release_id: releaseId || 0, // it should never happen here that `releaseId` is null
        },
        ctx,
      );

      return {
        redirect: {
          permanent: false,
          destination: `https://${formId}.paperform.co/?${queryParameters}`,
        },
      };
    } catch {
      // Do nothing
    }
  }

  const studyPrograms = await useStudyProgramsByCourse.fetchQuery(queryClient, {
    ctx,
    params: {
      field: "slug",
      value: courseSlug,
      expands: ["progress", "stats"],
    },
  });

  if (studyPrograms && studyPrograms.length === 1) {
    return {
      redirect: {
        permanent: false,
        destination: studyProgramPath(courseSlug, studyPrograms[0].short_slug),
      },
    };
  }

  if (userCourseAccess && userCourseAccess.access_type !== "NO_ACCESS") {
    await queryClient.prefetchQuery({
      queryKey: ["diagnosticsInfo", courseSlug],
      queryFn: () => fetchDiagnosticsInfo(courseSlug, ctx),
    });
  }

  return {
    props: {
      courseSlug,
      dehydratedState: dehydrate(queryClient),
      ...(await serverSideTranslations(ctx.locale || "es-AR", [
        "common",
        "layout-features",
        "side-nav",
        "pendingDiagnostic",
        "gamification",
        "notifications",
        "omni-search",
        "notifications-dock",
      ])),
    },
  };
};

export default CourseContentView;
